// Sepia's colours, papers and fonts.
import type { BoardBackground, FontKey, PaperKey, PatternKey } from "../../shared/model";

export interface Swatch {
  name: string;
  color: string;
}

/** Marker-cap inks, darkest first. */
export const INKS: Swatch[] = [
  { name: "Sepia", color: "#2b211a" },
  { name: "Burnt umber", color: "#7a4a2a" },
  { name: "Terracotta", color: "#c2583a" },
  { name: "Ochre", color: "#cf9234" },
  { name: "Olive", color: "#77843a" },
  { name: "Sage", color: "#4f8a5b" },
  { name: "Teal", color: "#2f7c79" },
  { name: "Slate blue", color: "#3f64a0" },
  { name: "Plum", color: "#7d4a86" },
  { name: "Rose", color: "#c5507a" },
  { name: "Graphite", color: "#6f6760" },
  { name: "Chalk", color: "#fbf6ec" }
];

export const HIGHLIGHTS: Swatch[] = [
  { name: "Butter", color: "#f3d36b" },
  { name: "Peach", color: "#f4a97f" },
  { name: "Mint", color: "#a9d9a0" },
  { name: "Sky", color: "#a8cbea" },
  { name: "Lilac", color: "#d0b9ec" },
  { name: "Blush", color: "#f2a5b8" }
];

/** Fills for shapes (soft watercolour tones). */
export const FILLS: Swatch[] = [
  { name: "Cream", color: "#fbf3e2" },
  { name: "Butter", color: "#f4d98c" },
  { name: "Apricot", color: "#f2c094" },
  { name: "Clay", color: "#e3a58a" },
  { name: "Blush", color: "#efb9b4" },
  { name: "Lilac", color: "#d5c8e6" },
  { name: "Sky", color: "#bcd3df" },
  { name: "Seafoam", color: "#b7d8cf" },
  { name: "Sage", color: "#c9d8b0" },
  { name: "Kraft", color: "#d9bf96" },
  { name: "Stone", color: "#cfc6bb" },
  { name: "Cocoa", color: "#8b6a52" }
];

export const NOTE_PAPERS: Swatch[] = [
  { name: "Butter", color: "#f6dc8f" },
  { name: "Apricot", color: "#f5c49a" },
  { name: "Blush", color: "#f3c0bb" },
  { name: "Sage", color: "#cfdcb6" },
  { name: "Sky", color: "#c4d9e4" },
  { name: "Lilac", color: "#dacdea" },
  { name: "Kraft", color: "#dcc39b" },
  { name: "Cream", color: "#fbf4e4" }
];

export const TAPES: Swatch[] = [
  { name: "Rose tape", color: "#d98c8c" },
  { name: "Terracotta tape", color: "#d0845f" },
  { name: "Mustard tape", color: "#d9b25a" },
  { name: "Sage tape", color: "#93ad86" },
  { name: "Denim tape", color: "#8aa3bf" },
  { name: "Lavender tape", color: "#ab9ac7" }
];

export const FONTS: Record<FontKey, { label: string; family: string; scale: number; lineHeight: number }> = {
  hand: { label: "hand", family: "'Caveat', 'Segoe Print', cursive", scale: 1.18, lineHeight: 1.14 },
  print: { label: "print", family: "'Kalam', 'Comic Sans MS', cursive", scale: 1, lineHeight: 1.3 },
  marker: { label: "MARKER", family: "'Permanent Marker', 'Kalam', cursive", scale: 0.95, lineHeight: 1.25 },
  type: { label: "type", family: "'Special Elite', 'Courier New', monospace", scale: 0.96, lineHeight: 1.35 },
  serif: { label: "serif", family: "'Fraunces Variable', Georgia, serif", scale: 0.98, lineHeight: 1.3 },
  sans: { label: "plain", family: "system-ui, -apple-system, 'Segoe UI', sans-serif", scale: 0.95, lineHeight: 1.35 },
  mono: { label: "mono", family: "'JetBrains Mono Variable', ui-monospace, monospace", scale: 0.88, lineHeight: 1.4 }
};

export interface PaperDef {
  label: string;
  color: string;
  line: string;
  major: string;
  ink: string;
  dark?: boolean;
}

export const PAPERS: Record<PaperKey, PaperDef> = {
  cream: { label: "Cream", color: "#f7f0e3", line: "rgba(165,128,86,0.16)", major: "rgba(165,128,86,0.27)", ink: "#2b211a" },
  kraft: { label: "Kraft", color: "#e6d2b0", line: "rgba(120,84,44,0.16)", major: "rgba(120,84,44,0.27)", ink: "#2b211a" },
  white: { label: "White", color: "#fdfbf6", line: "rgba(110,120,140,0.14)", major: "rgba(110,120,140,0.24)", ink: "#2b211a" },
  sage: { label: "Sage", color: "#e5eadb", line: "rgba(80,110,70,0.15)", major: "rgba(80,110,70,0.26)", ink: "#2b211a" },
  blush: { label: "Blush", color: "#f6e6df", line: "rgba(170,100,90,0.14)", major: "rgba(170,100,90,0.24)", ink: "#2b211a" },
  walnut: { label: "Walnut", color: "#2f2620", line: "rgba(240,220,190,0.07)", major: "rgba(240,220,190,0.13)", ink: "#f3e9d8", dark: true },
  slate: { label: "Chalkboard", color: "#2d3533", line: "rgba(220,235,225,0.07)", major: "rgba(220,235,225,0.13)", ink: "#eef2ea", dark: true }
};

export const PATTERNS: { key: PatternKey; label: string }[] = [
  { key: "grid", label: "Quadrille" },
  { key: "dots", label: "Dots" },
  { key: "lines", label: "Ruled" },
  { key: "iso", label: "Isometric" },
  { key: "blank", label: "Blank" }
];

function mixHex(a: string, b: string, t: number): string {
  const pa = [1, 3, 5].map((i) => parseInt(a.slice(i, i + 2), 16));
  const pb = [1, 3, 5].map((i) => parseInt(b.slice(i, i + 2), 16));
  return "#" + pa.map((v, i) => Math.round(v + (pb[i] - v) * t).toString(16).padStart(2, "0")).join("");
}

/** The desk a notebook's pages lie on: a shade deeper than the paper. */
export function deskColor(bg: BoardBackground): string {
  const paper = PAPERS[bg.paper] ?? PAPERS.cream;
  return paper.dark ? mixHex(paper.color, "#000000", 0.4) : mixHex(paper.color, "#6b5442", 0.2);
}

export function defaultInkFor(bg: BoardBackground): string {
  return PAPERS[bg.paper]?.ink ?? "#2b211a";
}

/** Readable text colour on a given fill. */
export function contrastInk(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return "#2b211a";
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return 0.299 * r + 0.587 * g + 0.114 * b > 140 ? "#2b211a" : "#fbf6ec";
}

// ---- ink that stays readable when the paper changes ----

function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function luminance([r, g, b]: [number, number, number]): number {
  const f = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

function invertLightness([r, g, b]: [number, number, number]): string {
  const rf = r / 255, gf = g / 255, bf = b / 255;
  const max = Math.max(rf, gf, bf), min = Math.min(rf, gf, bf);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    h = max === rf ? (gf - bf) / d + (gf < bf ? 6 : 0) : max === gf ? (bf - rf) / d + 2 : (rf - gf) / d + 4;
    h /= 6;
  }
  const L = Math.min(0.92, Math.max(0.12, 1 - l * 0.95));
  const q = L < 0.5 ? L * (1 + s) : L + s - L * s;
  const p = 2 * L - q;
  const hue = (t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const to = (v: number) => Math.round(v * 255).toString(16).padStart(2, "0");
  return "#" + to(hue(h + 1 / 3)) + to(hue(h)) + to(hue(h - 1 / 3));
}

const adaptCache = new Map<string, string>();

/**
 * The colour to draw an ink/stroke/text colour with on the current paper: near-black inks turn light on dark
 * papers and near-white inks turn dark on light papers (hue kept). The stored colour is never changed.
 */
export function adaptInk(color: string, darkPaper: boolean): string {
  const key = color + (darkPaper ? "|d" : "|l");
  const hit = adaptCache.get(key);
  if (hit) return hit;
  const rgb = hexToRgb(color);
  let out = color;
  if (rgb) {
    const lum = luminance(rgb);
    if (darkPaper && lum < 0.1) out = invertLightness(rgb);
    else if (!darkPaper && lum > 0.8) out = invertLightness(rgb);
  }
  adaptCache.set(key, out);
  return out;
}
